import * as React from "react";
import { Dropdown, Text, IconButton } from "@fluentui/react";
import { Draggable } from "react-beautiful-dnd";
import { Card } from "@uifabric/react-cards";
import Select from "react-select";
import { FixedSizeList as List } from "react-window";
import {
  ActionItemLabel,
  getActionLabel,
  stripHtml,
} from "../../ActionItems/ActionItemLabel";

const ROW_HEIGHT = 56;

const CustomMenuList = ({ children, ...props }) => {
  const itemCount = Array.isArray(children) ? children.length : 0;
  const totalHeight = Math.min(360, itemCount * ROW_HEIGHT);

  if (!Array.isArray(children) || itemCount === 0) {
    return <div>{children}</div>;
  }

  return (
    <List height={totalHeight} itemCount={itemCount} itemSize={ROW_HEIGHT} width="100%">
      {({ index, style }) => (
        <div
          style={{
            ...style,
            padding: "0 8px",
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          {children[index]}
        </div>
      )}
    </List>
  );
};

const selectStyles = {
  menuList: (base) => ({
    ...base,
    maxHeight: "360px",
    overflowY: "auto",
  }),
  container: (base) => ({
    ...base,
    width: 600,
  }),
  control: (base) => ({
    ...base,
    border: "1px solid #aaaaaa",
    minHeight: 56,
  }),
  option: (base) => ({
    ...base,
    paddingTop: 6,
    paddingBottom: 6,
  }),
};

export const ChecklistActionItemCard = ({
  actionItem,
  actionItemId,
  index,
  options,
  filteredOptions,
  actionItems,
  handleOnActionItemChange,
  handleOnRequirementChange,
  setActionItemToDelete,
  checklist,
  labelLookups,
}) => {
  const isRequiredOnly = checklist.completionRules === "Required Only";
  return (
    <Draggable
      key={actionItemId}
      draggableId={String(actionItemId)}
      index={index}
    >
      {(provided) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          draggable={true}
        >
          <Card
            horizontal
            tokens={{
              childrenMargin: 15,
              minWidth: isRequiredOnly ? 900 : 750,
            }}
            styles={{
              root: {
                backgroundColor: "#ffffff",
              },
            }}
          >
            <Card.Section
              styles={{
                root: {
                  alignSelf: "stretch",
                  justifyContent: "center",
                  width: 40,
                  borderRight: "1px solid #F3F2F1",
                },
              }}
            >
              <Text variant="xLarge">{actionItem.orderNumber + 1}</Text>
            </Card.Section>
            <Card.Item>
              <ActionItemsDropDown
                options={options}
                filteredOptions={filteredOptions}
                actionItem={actionItem}
                onChange={handleOnActionItemChange}
                actionItems={actionItems}
                labelLookups={labelLookups}
              />
            </Card.Item>
            <Card.Item>
              {isRequiredOnly && (
                <Dropdown
                  label="Requirement"
                  selectedKey={actionItem.requirement}
                  options={[
                    { key: "Required", text: "Required" },
                    { key: "Not Required", text: "Not Required" },
                  ]}
                  onChange={(e, item) =>
                    handleOnRequirementChange(e, item, actionItem.key)
                  }
                  styles={{ dropdown: { width: 150 } }}
                />
              )}
            </Card.Item>
            <Card.Section
              styles={{
                root: {
                  alignSelf: "stretch",
                  justifyContent: "center",
                  borderLeft: "1px solid #F3F2F1",
                },
              }}
              tokens={{ padding: "0px 0px 0px 12px" }}
            >
              <IconButton
                iconProps={{ iconName: "Delete" }}
                onClick={() => setActionItemToDelete(actionItemId)}
              />
            </Card.Section>
          </Card>
        </div>
      )}
    </Draggable>
  );
};

const ActionItemsDropDown = ({
  options,
  filteredOptions,
  actionItem,
  onChange,
  actionItems,
  labelLookups,
  ...rest
}) => {
  const currentOption = options.find((option) => {
    return option.key === actionItem.actionItemID;
  });

  const selectOptions = [...filteredOptions, currentOption]
    .filter(Boolean)
    .map((opt) => ({
      value: opt.key,
      label: opt.text,
      actionItem: opt.actionItem,
    }));

  const currentValue = actionItem.actionItemID
    ? selectOptions.find((opt) => opt.value === actionItem.actionItemID)
    : null;

  const formatOptionLabel = (option) => (
    <ActionItemLabel item={option.actionItem} lookups={labelLookups} />
  );

  const getOptionLabel = (option) => {
    const item = option.actionItem;
    if (!item) return option.label || "";
    const { action, target, chips, tooltip } = getActionLabel(item, labelLookups);
    return [
      action,
      target,
      `#${item.actionItemID}`,
      item.actionItemType,
      ...chips,
      stripHtml(item.actionItemTitle),
      tooltip,
    ]
      .filter(Boolean)
      .join(" ");
  };

  return (
    <div>
      <label className="ms-Label root-113" style={{ fontWeight: 600, padding: "5px 0" }}>
        Action Title
      </label>
      <Select
        components={{ MenuList: CustomMenuList }}
        options={selectOptions}
        placeholder="Select an option"
        value={currentValue}
        onChange={(option) => {
          const item = { key: option?.value, text: option?.label };
          onChange(item, actionItem);
        }}
        isSearchable
        styles={selectStyles}
        formatOptionLabel={formatOptionLabel}
        getOptionLabel={getOptionLabel}
        getOptionValue={(option) => option.value}
        {...rest}
      />
    </div>
  );
};
